/** Order record editor — one order, edited in place on the records core the booking
 *  record runs on (plan §2.4, §4.1, D10).
 *
 *  - `GET /orders/edit/catalog/` — the static field registry (stage, widget, what
 *    Ventrata's order webhook and a booking import do to each column), the effect
 *    catalogue and the choice questions (`linked_bookings_ref`: required, no default,
 *    raised by an order-ref change). Cached for an hour.
 *  - `GET /orders/{pk}/edit_context/` — the per-order facts: `baselines` (the `from` of
 *    every change), the restech verdicts and `rewritten_fields`, the linked bookings,
 *    who order events address, the caller's permissions.
 *  - `POST /orders/{pk}/changeset/preview/` — plans a changeset, writes nothing.
 *  - `POST /orders/{pk}/changeset/` — applies it in one transaction. 409 rejects as
 *    `OrderDriftError`, a 400 as `OrderChangesetValidationError`, and the field-keyed
 *    403 (`options.linked_bookings_ref: update` without console_bookings_edit) as one too
 *    (`code: forbidden`), so the editor files it under the choice.
 *
 *  The records core speaks one structural changeset (`RecordChangeset`, the booking
 *  wire shape); `toOrderChangeset` narrows it to this endpoint's (`entity: order`, field
 *  changes only), and `toRecordPreview` fills the preview keys an order plan has no use
 *  for (external writes, procedures).
 *
 *  Hand-authored (console convention); mirrors `OrderRecord*` in console.openapi.yaml
 *  (contract test: orderRecord.contract.test.ts).
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { RecordChangeset, RecordPreview } from '@/features/records/editor/types';

import type { BookingEditCatalogEffect, BookingEditEffectSummary, UpdateEvents } from './bookingEdit';
import {
  bookingRecordKeys,
  type BookingRecordChangedBy,
  type BookingRecordFollowUp,
  type BookingRecordRequiredChoice,
  type BookingRecordVerdict,
} from './bookingRecord';
import { bookingDetailKey, type BookingTenantRef, type UserRef } from './bookings';
import { apiGet, apiPost, ConsoleApiError } from './client';
import { orderDetailKey, type OrderDetail } from './orders';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** The spine panels in lifecycle order (OrderRecordStage). */
export const ORDER_RECORD_STAGES = ['placed', 'contact', 'bookings', 'money'] as const;
export type OrderRecordStage = (typeof ORDER_RECORD_STAGES)[number];

/** The editable fields — `OrderRecordChange.field`, in catalog order. */
export const ORDER_RECORD_EDITABLE_KEYS = [
  'booking_state',
  'active',
  'supplier_reference',
  'reseller_ref',
  'user',
  'tags',
  'order_ref',
  'custom_id',
  'contact_first_name',
  'contact_last_name',
  'contact_email',
  'contact_phone',
  'contact_company',
  'contact_country',
] as const;
export type OrderRecordEditableKey = (typeof ORDER_RECORD_EDITABLE_KEYS)[number];

export type OrderRecordFieldKind = 'editable' | 'vendor' | 'derived';

export type OrderRecordWidget = 'enum' | 'boolean' | 'text' | 'email' | 'country' | 'user' | 'tags';

/** What one writer does to an order column, statically. */
export type OrderRecordRule = 'rewritten' | 'filled_if_empty' | 'linked_if_empty' | 'by_category' | 'never';

export type OrderRecordOptionKey = 'update_events' | 'linked_bookings_ref';

/** The D10 answer: the linked bookings take the order's new ref, or keep theirs. */
export type OrderLinkedBookingsRef = 'update' | 'leave';

export type OrderRecordErrorCode =
  | 'disabled'
  | 'invalid_request'
  | 'too_many_changes'
  | 'entity_not_allowed'
  | 'unknown_field'
  | 'duplicate_change'
  | 'not_editable'
  | 'invalid_value'
  | 'required_choice'
  | 'unique_collision'
  | 'description_too_long'
  | 'forbidden';

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export interface OrderRecordChoice {
  value: number | string;
  label: string;
}

export interface OrderRecordCatalogField {
  /** Unique across the catalog (OrderRecordFieldKey); the `field` of a change. */
  key: string;
  entity: 'order';
  label: string;
  kind: OrderRecordFieldKind;
  stage: OrderRecordStage;
  /** The edit-mode input (kind = editable); null for the read-only kinds. */
  widget: OrderRecordWidget | null;
  choices: OrderRecordChoice[];
  nullable: boolean;
  max_length?: number | null;
  /** The value (account, tags) must belong to the order's tenant family. */
  tenant_scoped: boolean;
  effects: string[];
  automatic: string[];
  /** What Ventrata's order webhook does to it on an order Ventrata created. */
  ventrata_rule: OrderRecordRule;
  /** What a booking import does to it on any order. */
  import_rule: OrderRecordRule;
  /** Both rules in words. */
  restech_note: string;
  /** vendor / derived kinds: the ConsoleOrderDetail properties the field renders. */
  detail_keys: string[];
  help?: string;
  warning?: string;
}

export interface OrderRecordCatalogOption {
  key: OrderRecordOptionKey;
  /** events.update_events or options.linked_bookings_ref. */
  path: string;
  label: string;
  help?: string;
  type: 'choice';
  values: Array<{ value: string; label: string; warning?: string }>;
  /** update_events: emit; linked_bookings_ref: null (no default). */
  default: string | null;
  required: boolean;
  asked_when: string[];
  asked_when_note?: string;
}

export interface OrderRecordCatalog {
  /** The ConsoleOrderEditEnabled kill switch. */
  enabled: boolean;
  max_changes: number;
  description_max_length: number;
  event_tag_template: string;
  fields: OrderRecordCatalogField[];
  effects: BookingEditCatalogEffect[];
  options: OrderRecordCatalogOption[];
  /** OrderRecordRule code → explanation. */
  restech_rules: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Edit context
// ---------------------------------------------------------------------------

export interface OrderRecordRestech {
  /** Order.integration (e.g. ventrata); null when no integration created the order. */
  managed_by: string | null;
  managed_by_display: string | null;
  account: string | null;
  /** A booking import has written the order. */
  imported: boolean;
  /** The catalog fields the feed rewrites on every update — editable and read-only. */
  rewritten_fields: string[];
  /** Editable field key → verdict on this order (never `suspended`). */
  verdicts: Record<string, BookingRecordVerdict>;
}

export interface OrderRecordEditContext {
  order_id: number;
  generated_ts: string;
  flags: { order_edit_enabled: boolean };
  /** The caller's console permissions by codename. */
  permissions: { console_orders_edit: boolean; [codename: string]: boolean };
  tenant: BookingTenantRef;
  /** `"order:<id>"` → field key → current value, in the changeset wire format. */
  baselines: Record<string, Record<string, unknown>>;
  /** What an order-ref change with `linked_bookings_ref: update` rewrites. */
  linked_bookings: { count: number; ids: number[] };
  /** Who the order's events address now (Order.get_notification_user). */
  notification_user: UserRef | null;
  restech: OrderRecordRestech;
}

// ---------------------------------------------------------------------------
// Changeset (request)
// ---------------------------------------------------------------------------

export interface OrderRecordChange {
  entity: 'order';
  id: number;
  field: OrderRecordEditableKey;
  from: unknown;
  to: unknown;
}

export interface OrderRecordChangeset {
  description?: string | null;
  events?: { update_events?: UpdateEvents };
  options?: { linked_bookings_ref?: OrderLinkedBookingsRef };
  changes: OrderRecordChange[];
}

/** The records core's changeset as this endpoint takes it: field changes of the order
 *  (the core compiles nothing else for an order — no actions), its event policy and the
 *  linked-bookings answer. */
export function toOrderChangeset(body: RecordChangeset): OrderRecordChangeset {
  const events = body.events as { update_events?: UpdateEvents } | undefined;
  const options = body.options as { linked_bookings_ref?: OrderLinkedBookingsRef } | undefined;
  const out: OrderRecordChangeset = {
    changes: body.changes.flatMap((c) =>
      c.field ? [{ entity: 'order' as const, id: c.id, field: c.field as OrderRecordEditableKey, from: c.from, to: c.to }] : [],
    ),
  };
  if (body.description) out.description = body.description;
  if (events?.update_events) out.events = { update_events: events.update_events };
  if (options?.linked_bookings_ref) out.options = { linked_bookings_ref: options.linked_bookings_ref };
  return out;
}

// ---------------------------------------------------------------------------
// Preview / apply (responses)
// ---------------------------------------------------------------------------

export interface OrderRecordChangeOutcome {
  /** `"order:<id>:<field>"`, or `"booking:<id>:order_ref"` for a linked booking. */
  key: string;
  entity: 'order' | 'booking';
  id: number;
  field: string;
  /** `via_order` — not sent: a linked booking takes the order's new ref. */
  outcome: 'change' | 'no_change' | 'via_order';
  via_order_of?: number | null;
  from: unknown;
  to: unknown;
  from_display?: string | null;
  to_display?: string | null;
  verdict?: BookingRecordVerdict | null;
  notes?: string[];
}

export interface OrderRecordConflict {
  code: 'unique_collision' | 'drift';
  key: string;
  detail: string;
  other_order_id?: number | null;
  current?: unknown;
  changed_by?: BookingRecordChangedBy | null;
}

export interface OrderRecordEvent {
  /** order.updated, or tour_participant.updated for a linked booking whose ref moves. */
  code: string;
  entity: 'order' | 'booking';
  id: number;
  status: string;
  guest_visible: boolean;
  tag?: string | null;
  logid?: string | null;
  event_delivery_uuid?: string | null;
  detail?: string | null;
}

export interface OrderRecordPreview {
  changes: OrderRecordChangeOutcome[];
  required_choices: BookingRecordRequiredChoice[];
  conflicts: OrderRecordConflict[];
  effects: Record<string, BookingEditEffectSummary>;
  events: OrderRecordEvent[];
  warnings: string[];
}

export interface OrderRecordApplyResult {
  audit_id: number;
  audit_uuid: string;
  event_tag: string;
  saved_ts: string;
  changes: OrderRecordChangeOutcome[];
  events: OrderRecordEvent[];
  follow_ups: BookingRecordFollowUp[];
  warnings: string[];
  /** Re-seed the page from these two. */
  detail: OrderDetail;
  edit_context: OrderRecordEditContext;
}

/** An order plan as the ledger reads it: an order save writes nothing outside the
 *  database and runs no procedures beyond its effects. */
export function toRecordPreview(p: OrderRecordPreview): RecordPreview {
  return { ...p, external: [], follow_ups: [] };
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export interface OrderRecordDriftedField {
  key: string;
  entity: 'order';
  id: number;
  field: string;
  current: unknown;
  changed_by?: BookingRecordChangedBy | null;
}

export interface OrderRecordFieldError {
  code: OrderRecordErrorCode;
  message: string;
  /** unique_collision: the order that already has the value. */
  other_order_id?: number | null;
}

/** 409 of the apply: some `from` no longer matches. Nothing was written. */
export class OrderDriftError extends ConsoleApiError {
  constructor(
    status: number,
    body: ConsoleApiError['body'],
    message: string,
    public readonly drifted: OrderRecordDriftedField[],
  ) {
    super(status, body, message);
  }
}

/** 400 of the preview or apply — and the field-keyed 403 (`code: forbidden`, `missing`
 *  the permissions lacked). `errors` is keyed `"order:<id>:<field>"`,
 *  `"options.linked_bookings_ref"`, `"events.update_events"`, `"description"` or
 *  `"changes"`. Nothing was written. */
export class OrderChangesetValidationError extends ConsoleApiError {
  constructor(
    status: number,
    body: ConsoleApiError['body'],
    message: string,
    public readonly code: OrderRecordErrorCode,
    public readonly errors: Record<string, OrderRecordFieldError[]>,
    public readonly missing: string[] = [],
  ) {
    super(status, body, message);
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Re-type a changeset call's ConsoleApiError by its body; anything else passes through.
 *  A 403 without a field-keyed body (no console_orders_edit) stays a plain error. */
export function toOrderChangesetError(e: unknown): unknown {
  if (!(e instanceof ConsoleApiError)) return e;
  const body: unknown = e.body;
  if (!isPlainObject(body)) return e;
  if (e.status === 409 && body.code === 'drift' && Array.isArray(body.drifted)) {
    return new OrderDriftError(e.status, e.body, e.message, body.drifted as OrderRecordDriftedField[]);
  }
  if ((e.status === 400 || e.status === 403) && typeof body.code === 'string' && isPlainObject(body.errors)) {
    return new OrderChangesetValidationError(
      e.status,
      e.body,
      e.message,
      body.code as OrderRecordErrorCode,
      body.errors as Record<string, OrderRecordFieldError[]>,
      Array.isArray(body.missing) ? (body.missing as string[]) : [],
    );
  }
  return e;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export function fetchOrderRecordCatalog(): Promise<OrderRecordCatalog> {
  return apiGet<OrderRecordCatalog>('/orders/edit/catalog/');
}

export function fetchOrderEditContext(id: number, signal?: AbortSignal): Promise<OrderRecordEditContext> {
  return apiGet<OrderRecordEditContext>(`/orders/${id}/edit_context/`, signal ? { signal } : undefined);
}

export async function previewOrderChangeset(
  id: number,
  body: OrderRecordChangeset,
  signal?: AbortSignal,
): Promise<OrderRecordPreview> {
  try {
    return await apiPost<OrderRecordPreview>(`/orders/${id}/changeset/preview/`, body, signal ? { signal } : undefined);
  } catch (e) {
    throw toOrderChangesetError(e);
  }
}

export async function applyOrderChangeset(id: number, body: OrderRecordChangeset): Promise<OrderRecordApplyResult> {
  try {
    return await apiPost<OrderRecordApplyResult>(`/orders/${id}/changeset/`, body);
  } catch (e) {
    throw toOrderChangesetError(e);
  }
}

// ---------------------------------------------------------------------------
// React Query
// ---------------------------------------------------------------------------

/** The catalog only changes with a deploy. */
export const ORDER_RECORD_CATALOG_STALE_MS = 60 * 60_000;

export const orderRecordKeys = {
  all: ['orderRecord'] as const,
  catalog: () => ['orderRecord', 'catalog'] as const,
  editContext: (id: number) => ['orderRecord', 'editContext', id] as const,
  preview: (id: number, body: OrderRecordChangeset) => ['orderRecord', 'preview', id, body] as const,
};

export function useOrderRecordCatalog(): UseQueryResult<OrderRecordCatalog> {
  return useQuery({
    queryKey: orderRecordKeys.catalog(),
    queryFn: fetchOrderRecordCatalog,
    staleTime: ORDER_RECORD_CATALOG_STALE_MS,
  });
}

export function useOrderEditContext(
  id: number,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<OrderRecordEditContext> {
  return useQuery({
    queryKey: orderRecordKeys.editContext(id),
    queryFn: ({ signal }) => fetchOrderEditContext(id, signal),
    enabled: enabled && Number.isFinite(id),
  });
}

/** The ledger's preview of a (debounced) compiled draft; `null` = nothing to preview —
 *  the booking preview's query semantics (keyed by the body, the previous plan kept on
 *  screen while the next loads), answered in the records core's preview shape. */
export function useOrderChangesetPreview(
  id: number,
  body: RecordChangeset | null,
): UseQueryResult<RecordPreview> {
  const wire = body ? toOrderChangeset(body) : { changes: [] };
  return useQuery({
    queryKey: orderRecordKeys.preview(id, wire),
    queryFn: ({ signal }) => previewOrderChangeset(id, wire, signal),
    select: toRecordPreview,
    enabled: body !== null && wire.changes.length > 0 && Number.isFinite(id),
    staleTime: 0,
    gcTime: 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/** Save. `onSaved` runs first (the draft resets before the re-seeded baselines rebase
 *  it), then the edit context and the order detail are re-seeded from the response. The
 *  linked bookings whose ref the save moved are refetched where they are cached. A 409
 *  rejects with `OrderDriftError` (the edit context is refetched for the rebase), a 400 /
 *  field-keyed 403 with `OrderChangesetValidationError`. */
export function useApplyOrderChangeset(
  id: number,
  { onSaved }: { onSaved?: (result: OrderRecordApplyResult) => void } = {},
): UseMutationResult<OrderRecordApplyResult, unknown, RecordChangeset> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordChangeset) => applyOrderChangeset(id, toOrderChangeset(body)),
    onSuccess: (result) => {
      onSaved?.(result);
      qc.setQueryData(orderRecordKeys.editContext(id), result.edit_context);
      qc.setQueryData(orderDetailKey(id), result.detail);
      for (const c of result.changes) {
        if (c.entity !== 'booking' || c.outcome !== 'via_order') continue;
        void qc.invalidateQueries({ queryKey: bookingDetailKey(c.id) });
        void qc.invalidateQueries({ queryKey: bookingRecordKeys.editContext(c.id) });
      }
    },
    onError: (e) => {
      if (e instanceof OrderDriftError) {
        void qc.invalidateQueries({ queryKey: orderRecordKeys.editContext(id) });
      }
    },
  });
}
